import { supabase } from "@/integrations/supabase/client";
import { addBackManualInventoryForOrder } from "@/services/inventory/manualInventoryService";
import { NotificationType } from "@/types/notifications";

/**
 * Cancel a customer order with time-based validation
 * @param orderId - The order ID to cancel
 * @param reason - The reason for cancellation
 * @returns Promise with success status and message
 */
export const cancelCustomerOrder = async (
  orderId: string,
  reason: string
): Promise<{ success: boolean; error?: string; message?: string }> => {
  try {
    // First, get the order to check its creation time and status
    const { data: order, error: fetchError } = await supabase
      .from("orders")
      .select("*")
      .eq("id", orderId)
      .single();

    if (fetchError) {
      console.error("Error fetching order:", fetchError);
      return { success: false, error: "Order not found" };
    }

    if (!order) {
      return { success: false, error: "Order not found" };
    }

    // Check if order is already cancelled or completed
    if (order.status === "cancelled" || order.status === "completed") {
      return { success: false, error: "Order cannot be cancelled" };
    }

    // Check if order is delivered
    if (order.status === "delivered") {
      return { success: false, error: "Order has already been delivered" };
    }

    // Calculate time since order creation
    const orderCreatedAt = new Date(order.created_at);
    const now = new Date();
    const timeDiffInMinutes = (now.getTime() - orderCreatedAt.getTime()) / (1000 * 60);

    // If more than 5 minutes have passed, don't allow cancellation
    if (timeDiffInMinutes > 5) {
      return { 
        success: false, 
        error: "orderTooLateToCancel",
        message: "Order cannot be cancelled after 5 minutes. Please contact staff for assistance."
      };
    }

    // Check if order was ever prepared (preparing or ready status)
    const wasEverPrepared = order.status === "preparing" || order.status === "ready";

    // Update the order status to cancelled
    const { error: updateError } = await supabase
      .from("orders")
      .update({ 
        status: "cancelled", 
        cancel_reason: reason 
      })
      .eq("id", orderId);

    if (updateError) {
      console.error("Error cancelling order:", updateError);
      return { success: false, error: "Failed to cancel order" };
    }

    // Add back manual inventory if the order was ever prepared
    if (wasEverPrepared) {
      const inventoryResult = await addBackManualInventoryForOrder(orderId);
      if (!inventoryResult.success) {
        console.error(
          "Error adding back manual inventory:",
          inventoryResult.error
        );
        // Continue with cancellation even if inventory fails
      }
    }

    return { 
      success: true, 
      message: "Order cancelled successfully" 
    };
  } catch (error) {
    console.error("Error in cancelCustomerOrder:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error cancelling order"
    };
  }
};

/**
 * Request waiter assistance for order cancellation
 * @param orderId - The order ID
 * @param reason - The reason for wanting to cancel
 * @returns Promise with success status
 */
export const requestWaiterForCancellation = async (
  orderId: string,
  reason: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get the order details
    const { data: order, error: fetchError } = await supabase
      .from("orders")
      .select("*, store_id, table_id, table_name, table_number")
      .eq("id", orderId)
      .single();

    if (fetchError || !order) {
      console.error("Error fetching order:", fetchError);
      return { success: false, error: "Order not found" };
    }

    // Create a waiter request notification
    const { error: notificationError } = await supabase
      .from("notifications")
      .insert({
        store_id: order.store_id,
        entity_id: orderId,
        entity_type: "order",
        type: NotificationType.WAITER_REQUEST,
        title: "Customer requests waiter assistance",
        message: `Customer at Table ${order.table_number} (${order.table_name}) requests assistance to cancel order #${orderId.slice(0, 8)}. Reason: ${reason}`,
        recipient_email: "staff@store.com", // Placeholder email for staff notifications
        status: "unread",
        metadata: {
          orderId: orderId,
          tableNumber: order.table_number,
          tableName: order.table_name,
          reason: reason,
          action: "cancellation_request"
        }
      });

    if (notificationError) {
      console.error("Error creating waiter request:", notificationError);
      return { success: false, error: "Failed to request waiter assistance" };
    }

    return { success: true };
  } catch (error) {
    console.error("Error in requestWaiterForCancellation:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error requesting waiter"
    };
  }
}; 